import React, { useEffect, useState } from 'react';
import {
  Box,
  HeadingText,
  GDSStyleObject,
  FormField,
  TextInput,
  StackLayout,
  ParagraphText,
  Checkbox,
  Fieldset,
} from '@leagueplatform/genesis-core';
import { updateFormData } from '../../../helpers';

export function TransactionTypeInformation(props: any) {
  const { errorHandling, nextButtonClicked, footerToBottom } = props;

  const [form, setForm] = useState({
    dentalPlanName: 'United Concordia Dental',
    dentalPlanAddress1: 'PO Box 69421',
    dentalPlanAddress2: '4401 Deer Path Road',
    dentalPlanCity: 'Harrisburg',
    dentalPlanState: 'PA',
    dentalPlanZipCode: '17110',
    statementOfServices: false,
    requestForPredetermination: false,
    earlyAndPeriodicScreening: false,
  });

  const [isTransactionTypeError, setIsTransactionTypeError] = useState(false);
  const [transactionTypeError, setTransactionTypeError] = useState('');

  const formField =
    !form.statementOfServices &&
    !form.requestForPredetermination &&
    !form.earlyAndPeriodicScreening;

  const isValid =
    form.statementOfServices ||
    form.requestForPredetermination ||
    form.earlyAndPeriodicScreening;

  useEffect(() => {
    footerToBottom(false);
  }, [footerToBottom]);

  useEffect(() => {
    if (formField) {
      errorHandling(false);
    } else {
      errorHandling(true);
    }
  }, [errorHandling, formField]);

  useEffect(() => {
    if (nextButtonClicked) {
      if (!isValid) {
        setIsTransactionTypeError(true);
        setTransactionTypeError('This selection is required.');
      } else {
        setIsTransactionTypeError(false);
        setTransactionTypeError('');
      }
    }
  }, [isValid, nextButtonClicked]);

  const onUpdate = (e: any) => {
    const { checked, id } = e.target;
    const nextFormState = {
      ...form,
      [id]: checked,
    };
    if (
      !nextFormState.statementOfServices &&
      !nextFormState.requestForPredetermination &&
      !nextFormState.earlyAndPeriodicScreening
    ) {
      setIsTransactionTypeError(true);
      setTransactionTypeError('This selection is required.');
    } else {
      setIsTransactionTypeError(false);
      setTransactionTypeError('');
    }
    setForm(nextFormState);
  };

  useEffect(() => {
    const pInfo = JSON.parse(sessionStorage.getItem('formData') || '{}');
    if (pInfo) {
      setForm({
        dentalPlanName: pInfo?.dentalPlanName || 'United Concordia Dental',
        dentalPlanAddress1: pInfo?.dentalPlanAddress1 || 'PO Box 69421',
        dentalPlanAddress2: pInfo?.dentalPlanAddress2 || '4401 Deer Path Road',
        dentalPlanCity: pInfo?.dentalPlanCity || 'Harrisburg',
        dentalPlanState: pInfo?.dentalPlanState || 'PA',
        dentalPlanZipCode: pInfo?.dentalPlanZipCode || '17110',
        statementOfServices: pInfo?.statementOfServices || false,
        requestForPredetermination: pInfo?.requestForPredetermination || false,
        earlyAndPeriodicScreening: pInfo?.earlyAndPeriodicScreening || false,
      });
    }
  }, []);

  useEffect(() => {
    if (form) {
      updateFormData(form);
    }
  }, [form]);

  return (
    <StackLayout
      css={
        {
          backgroundColor: 'White',
          maxWidth: '800px',
        } as GDSStyleObject
      }
      horizontalAlignment="center"
    >
      <Box
        css={
          {
            display: 'flex',
            flexDirection: 'column',
            padding: '$three $two $two $two',
          } as GDSStyleObject
        }
      >
        <Box
          css={
            {
              display: 'flex',
              flexDirection: 'column',
              padding: '$none $two',
            } as GDSStyleObject
          }
        >
          <HeadingText
            level="1"
            size={{
              '@initial': 'xxl',
              '@mobile': 'xl',
              '@mobileLandscape': 'xl',
              '@tablet': 'xl',
              '@laptop': 'xxl',
              '@desktop': 'xxl',
            }}
          >
            Member Dental Claim Form
          </HeadingText>
        </Box>

        <Box
          css={
            {
              display: 'flex',
              flexDirection: 'column',
              padding: '$one $two',
              width: '100%',
            } as GDSStyleObject
          }
        >
          <Box
            css={
              {
                display: 'flex',
                flexDirection: 'column',
              } as GDSStyleObject
            }
          >
            <HeadingText
              data-testid="transactionTitle"
              level="3"
              size={{
                '@initial': 'xl',
                '@mobile': 'lg',
                '@mobileLandscape': 'lg',
                '@tablet': 'xl',
                '@laptop': 'xl',
                '@desktop': 'xl',
              }}
            >
              Step 1: Transaction Type
            </HeadingText>
            <ParagraphText
              css={
                {
                  marginTop: '$one',
                  '@mobile': { width: 'auto' },
                  '@desktop': { width: '600px' },
                  '@laptop': { width: '600px' },
                  '@mobileLandscape': { width: '600px' },
                  '@tablet': { width: '600px' },
                } as GDSStyleObject
              }
            >
              <p style={{ margin: 0 }}>
                Please select the appropriate claim form.
              </p>
            </ParagraphText>
          </Box>
          <Box
            css={
              {
                marginBottom: '$one',
              } as GDSStyleObject
            }
          >
            <Fieldset
              css={
                {
                  display: 'flex',
                  flexDirection: 'column',
                  padding: '$one $none',
                } as GDSStyleObject
              }
              legend={undefined}
              inputStatus={isTransactionTypeError ? 'error' : undefined}
              statusMessage={transactionTypeError}
              id="transactionType"
            >
              <Checkbox
                id="statementOfServices"
                name="statementOfServcies"
                label="Statement of actual services"
                checked={form.statementOfServices}
                onChange={(e) => onUpdate(e)}
                hint="If services have been performed, mark this box."
              />
              <Checkbox
                id="requestForPredetermination"
                name="predeterminationRequest"
                label="Request for Predetermination/Preauthorization"
                checked={form.requestForPredetermination}
                onChange={(e) => onUpdate(e)}
                hint="If there are no dates of service on receipts, mark this box."
              />
              <Checkbox
                id="earlyAndPeriodicScreening"
                name="earlyAndPeriodicScreening"
                label="Early and Periodic Screening, Diagnosis and Treatment Program/Title XIX"
                checked={form.earlyAndPeriodicScreening}
                onChange={(e) => onUpdate(e)}
                hint="If the claim is through EPSDT, mark this box."
              />
            </Fieldset>
          </Box>
          <Box>
            <HeadingText
              level="3"
              size={{
                '@initial': 'xl',
                '@mobile': 'lg',
                '@mobileLandscape': 'lg',
                '@tablet': 'xl',
                '@laptop': 'xl',
                '@desktop': 'xl',
              }}
            >
              Insurance Company/Dental Plan Information
            </HeadingText>
            <Box
              css={
                {
                  display: 'flex',
                  flexDirection: 'column',
                  padding: '$one $none',
                } as GDSStyleObject
              }
            >
              <FormField id="dentalPlanName" label="Comapny/Plan Name">
                <TextInput disabled value={form.dentalPlanName} />
              </FormField>
            </Box>
            <Box
              css={
                {
                  display: 'flex',
                  flexDirection: 'column',
                  padding: '$one $none',
                } as GDSStyleObject
              }
            >
              <FormField id="dentalPlanAddress1" label="Company Address Line 1">
                <TextInput disabled value={form.dentalPlanAddress1} />
              </FormField>
            </Box>
            <Box
              css={
                {
                  display: 'flex',
                  flexDirection: 'column',
                  padding: '$one $none',
                } as GDSStyleObject
              }
            >
              <FormField
                id="dentalPlanAddress2"
                label="Company Address Line 2 (Optional)"
              >
                <TextInput disabled value={form.dentalPlanAddress2} />
              </FormField>
            </Box>
            <Box
              css={
                {
                  display: 'flex',
                  flexDirection: 'column',
                  padding: '$one $none',
                } as GDSStyleObject
              }
            >
              <FormField id="dentalPlanCity" label="City">
                <TextInput disabled value={form.dentalPlanCity} />
              </FormField>
            </Box>
            <Box
              css={
                {
                  display: 'flex',
                  flexDirection: 'column',
                  padding: '$one $none',
                } as GDSStyleObject
              }
            >
              <FormField id="dentalPlanState" label="State">
                <TextInput value={form.dentalPlanState} disabled />
              </FormField>
            </Box>
            <Box
              css={
                {
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  padding: '$one $none',
                } as GDSStyleObject
              }
            >
              <FormField id="dentalPlanZipCode" label="Zip Code">
                <TextInput disabled value={form.dentalPlanZipCode} />
              </FormField>
            </Box>
          </Box>
        </Box>
      </Box>
    </StackLayout>
  );
}
